import React, { memo, useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  Form,
  Row,
  Spinner,
} from "react-bootstrap";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { useTranslation } from "react-i18next";
import debounce from "lodash.debounce";
import AsyncSelect from "react-select/async";
import Select from "react-select";

import { Creators as NotificationsActions } from "./../../store/ducks/notifications";

import { isScopes } from "./../../services/Auth";
import { maskCPF } from "./../../utils/CPF";
import { maskCNPJ } from "./../../utils/CNPJ";
import { isValidForm } from "./../../utils/Form";
import { isEmpty, ucFirstAll } from "./../../utils/String";
import { clearObject, mergeObject } from "./../../utils/Object";

import ChassiService from "./../../services/Chassi";
import ChassiSchema from "./../../services/Chassi/Schema";
import ClientService from "./../../services/Client";
import ClientSchemaReduced from "./../../services/Client/SchemaReduced";

const Chassi = ({
  options,
  props: { currentUser },
  funcs: { addNotice }
}) => {
  const { t } = useTranslation();
  const chassiService = new ChassiService();
  const clientService = new ClientService();

  const {
    chassi: __chassi = {},
    close: __close = () => {}
  } = options;

  const [chassi, setChassi] = useState(mergeObject(JSON.parse(JSON.stringify(ChassiSchema)), __chassi));

  useEffect(() => {
    setTimeout(() => {
      if(!isScopes({ currentUser, scopes: [
        "is:master",
        "read:chassis:all"
      ], every: false })) {
        setClientSelectedOption({
          dataAux: mergeObject(JSON.parse(JSON.stringify(ClientSchemaReduced)), currentUser.client),
          label: currentUser.client.type === "LEG" ? (`${currentUser.client.legal.socialReason} | CNPJ: ${maskCNPJ(currentUser.client.legal.cnpj)}`) : (`${currentUser.client.personal.name} | CPF: ${maskCPF(currentUser.client.personal.cpf)}`),
          value: currentUser.client.id
        });
        setChassi(prevState => ({
          ...prevState,
          client: mergeObject(JSON.parse(JSON.stringify(ClientSchemaReduced)), currentUser.client)
        }));
      }
    }, 1000);
  }, []); /* eslint-disable-line */

  const [clientSelectedOption, setClientSelectedOption] = useState(chassi.client.id === "" ? null : {
    dataAux: mergeObject(JSON.parse(JSON.stringify(ClientSchemaReduced)), chassi.client),
    label: chassi.client.type === "LEG" ? (`${chassi.client.legal.socialReason} | CNPJ: ${maskCNPJ(chassi.client.legal.cnpj)}`) : (`${chassi.client.personal.name} | CPF: ${maskCPF(chassi.client.personal.cpf)}`),
    value: chassi.client.id
  });

  const [activeOptions] = useState([
    { value: true, label: t("Active") },
    { value: false, label: t("Inactive") }
  ]);

  const [running, setRunning] = useState(false);

  const buttonDisabled = () => {
    if(!running) {
      return false;
    }
    return true;
  };

  const clientLoadOptions = (e, c) => {
    clientLoadOptionsDebounce(e, c);
  };

  const clientLoadOptionsDebounce = debounce(async (e, c) => {
    const clients = await clientService.findAutocomplete({ data: {
      search: e
    }, token: currentUser.token });
    c(filterClient(clients));
  }, 1000);

  const filterClient = (clients) => {
    return clients.map(e => {
      return {
        dataAux: e,
        label: e.type === "LEG" ? (`${e.legal.socialReason} | CNPJ: ${maskCNPJ(e.legal.cnpj)}`) : (`${e.personal.name} | CPF: ${maskCPF(e.personal.cpf)}`),
        value: e.id
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if(!running) {
      setRunning(true);
      let { valid, message } = isValidForm(e);
      if(!valid) {
        if(!message) {
          message = t("Error.Fields.Empty");
        }
        addNotice({
          title: t("Title.Chassi"),
          content: message
        });
      }
      else {
        let response = false;
        if(isCreate()) {
          response = await chassiService.create({
            data: handleSubmitForm({ chassi }),
            token: currentUser.token
          });
        }
        else {
          response = await chassiService.update({
            id: chassi.id,
            data: handleSubmitForm({ chassi }),
            token: currentUser.token
          });
        }

        if(response.success) {
          addNotice({
            title: t("Title.Chassi"),
            content: isCreate() ? t("Success.Insert") : t("Success.Update")
          });
          setTimeout(() => {
            __close();
          }, 200);
        }
        else {
          addNotice({
            title: t("Title.Chassi"),
            content: t(`Error.Chassi.${response.error.type}.${ucFirstAll(response.error.details[0].path)}`)
          });
        }
      }
    }
    setRunning(false);
  };

  const handleSubmitForm = ({ chassi }) => {
    let newChassi = Object.assign({}, JSON.parse(JSON.stringify(chassi)));
    newChassi = clearObject({ data: newChassi });
    newChassi.id = undefined;
    return newChassi;
  };

  const inputDisabled = () => {
    if(!running) {
      return false;
    }
    return true;
  };

  const isCreate = () => {
    return chassi.id === "";
  };

  return (
    <Form id="forms-chassi" noValidate onSubmit={handleSubmit}>
      <Card>
        <Card.Header>{t("Title.GeneralData")}</Card.Header>
        <Card.Body>
          <Row>
            <Col xs={12} md={6}>
              <Form.Group controlId="forms-chassi-active">
                <Form.Label>{t("Label.Active")}:</Form.Label>
                <Select
                  className="menu-outer-bottom"
                  classNamePrefix="select"
                  options={activeOptions}
                  isDisabled={inputDisabled()}
                  onChange={e => {
                    const active = e.value;
                    setChassi(prevState => ({
                      ...prevState,
                      active
                    }));
                  }}
                  value={activeOptions.find(x => x.value === chassi.active)}
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group controlId="forms-user-client">
                <Form.Label>{t("Title.Client")}:</Form.Label>
                <AsyncSelect
                  className={`menu-outer-bottom ${!isEmpty(chassi.client.id) ? "is-valid" : "is-invalid"}`}
                  classNamePrefix="select"
                  cacheOptions
                  defaultOptions
                  isDisabled={inputDisabled() || !isScopes({
                    currentUser,
                    scopes: [
                      "is:master",
                      "read:chassis:all"
                    ],
                    every: false
                  })}
                  loadOptions={clientLoadOptions}
                  loadingMessage={() => t("React.Select.Wait")}
                  noOptionsMessage={() => t("React.Select.NoOptions")}
                  onChange={e => {
                    if(e === null) {
                      setClientSelectedOption(e);
                      setChassi(prevState => ({
                        ...prevState,
                        client: JSON.parse(JSON.stringify(ClientSchemaReduced))
                      }));
                    }
                    else {
                      const { dataAux, label, value } = e;
                      setClientSelectedOption({ dataAux, label, value });
                      setChassi(prevState => ({
                        ...prevState,
                        client: mergeObject(JSON.parse(JSON.stringify(ClientSchemaReduced)), dataAux)
                      }));
                    }
                  }}
                  placeholder={t("Title.Client.Select")}
                  value={clientSelectedOption}
                  required={true}
                />
                <Form.Control.Feedback className={`${!isEmpty(chassi.client.id) ? "hide" : "show"}`} type="invalid">{t("Feedback.Client")}</Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col>
              <Form.Group controlId="forms-chassi-serial">
                <Form.Label>{t("Label.Serial")}:</Form.Label>
                <Form.Control
                  type="text"
                  placeholder={t("Label.Serial")}
                  disabled={inputDisabled()}
                  onChange={e => {
                    const serial = e.target.value;
                    setChassi(prevState => ({
                      ...prevState,
                      serial: serial.toUpperCase()
                    }));
                  }}
                  autoComplete="off"
                  value={chassi.serial}
                  isValid={!isEmpty(chassi.serial)}
                  isInvalid={isEmpty(chassi.serial)}
                  required={true}
                />
                <Form.Control.Feedback type="invalid">{t("Feedback.Serial")}</Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      <Form.Group className="default-form-button">
        <Button
          variant="dark"
          type="submit"
          disabled={buttonDisabled()}
        >
          {running ? <Spinner animation="border" size="sm" /> : t("Button.Save")}
        </Button>
      </Form.Group>
    </Form>
  );
};

const mapStateToProps = state => ({
  props: {
    currentUser: state.users.currentUser
  }
});

const mapDispatchToProps = dispatch => ({
  funcs: bindActionCreators(Object.assign({}, NotificationsActions), dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(memo(Chassi));
